/**
 * PropertyGrid Component
 * Displays a grid of property cards with loading and empty states
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useGetPublicPropertiesQuery } from "../../store/api/publicPropertiesApi";
import type { PropertyFilters } from "../../types";
import Icon from "../AppIcon";
import Button from "../ui/Button";
import Loader from "../ui/Loader";
import PropertyCard from "./PropertyCard";

interface PropertyGridProps {
	filters?: PropertyFilters;
}

const PropertyGrid: React.FC<PropertyGridProps> = ({ filters }) => {
	const [page, setPage] = useState(1);
	const [properties, setProperties] = useState<any[]>([]);
	const [hasMore, setHasMore] = useState(false);

	// Reset page to 1 when filters change
	useEffect(() => {
		setPage(1);
		setProperties([]);
	}, [filters]);

	const mergedFilters = useMemo(
		() => ({
			...(filters || {}),
			page,
			page_size: 9,
		}),
		[filters, page],
	);

	const { data, isLoading, isFetching, isError, error, refetch } =
		useGetPublicPropertiesQuery(mergedFilters);

	// Log errors for debugging
	useEffect(() => {
		if (isError && error) {
			console.error("PropertyGrid fetch error:", error);
			if ("data" in error) {
				console.error("Error data:", error.data);
			}
			if ("status" in error) {
				console.error("Error status:", error.status);
			}
		}
	}, [isError, error]);

	const loadMoreRef = useRef<HTMLDivElement | null>(null);

	// Merge pages into a single list
	useEffect(() => {
		if (!data) return;

		setProperties((prev) => {
			// If we're on the first page, replace; otherwise append
			if (page === 1) {
				return data.results || [];
			}

			const existingIds = new Set(prev.map((p) => p.id));
			const newItems = (data.results || []).filter(
				(p) => !existingIds.has(p.id),
			);
			return [...prev, ...newItems];
		});

		// Check if there are more pages
		// Backend returns next as a string like "?page=2" or null
		const hasNextPage =
			data.next !== null && data.next !== undefined && data.next !== "";
		const totalCount = data.count || 0;
		const currentResultsCount = data.results?.length || 0;
		const currentTotal =
			page === 1
				? currentResultsCount
				: properties.length + currentResultsCount;
		setHasMore(hasNextPage || currentTotal < totalCount);
	}, [data, page]);

	// Infinite scroll using IntersectionObserver
	useEffect(() => {
		if (!hasMore || isFetching) return;

		const node = loadMoreRef.current;
		if (!node) return;

		const observer = new IntersectionObserver(
			(entries) => {
				const [entry] = entries;
				if (entry.isIntersecting && hasMore && !isFetching) {
					setPage((prev) => prev + 1);
				}
			},
			{
				root: null,
				rootMargin: "200px",
				threshold: 0.1,
			},
		);

		observer.observe(node);

		return () => {
			observer.disconnect();
		};
	}, [hasMore, isFetching]);

	// Loading skeleton
	const LoadingSkeleton = () => (
		<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
			{[1, 2, 3, 4, 5, 6].map((i) => (
				<div
					key={i}
					className="bg-card border border-border rounded-xl overflow-hidden animate-pulse"
				>
					<div className="aspect-video bg-muted"></div>
					<div className="p-5 space-y-3">
						<div className="h-6 bg-muted rounded w-3/4"></div>
						<div className="h-4 bg-muted rounded w-1/2"></div>
						<div className="h-8 bg-muted rounded w-2/3"></div>
						<div className="flex gap-3">
							<div className="h-4 bg-muted rounded w-16"></div>
							<div className="h-4 bg-muted rounded w-16"></div>
							<div className="h-4 bg-muted rounded w-16"></div>
						</div>
						<div className="flex gap-2 pt-3">
							<div className="h-10 bg-muted rounded flex-1"></div>
							<div className="h-10 bg-muted rounded flex-1"></div>
						</div>
					</div>
				</div>
			))}
		</div>
	);

	// Empty state
	const EmptyState = () => (
		<div className="text-center py-16">
			<div className="inline-flex items-center justify-center w-24 h-24 bg-muted rounded-full mb-6">
				<Icon
					name="Search"
					size={48}
					className="text-muted-foreground opacity-50"
				/>
			</div>
			<h3 className="text-2xl font-semibold text-foreground mb-3">
				No Properties Found
			</h3>
			<p className="text-muted-foreground mb-6 max-w-md mx-auto">
				We couldn't find any properties matching your search criteria. Try
				adjusting your filters or clearing them to see all available properties.
			</p>
			<Button variant="outline" onClick={() => refetch()}>
				<Icon name="RefreshCw" size={16} className="mr-2" />
				Refresh Results
			</Button>
		</div>
	);

	// Error state
	const ErrorState = () => (
		<div className="text-center py-16">
			<div className="inline-flex items-center justify-center w-24 h-24 bg-error/10 rounded-full mb-6">
				<Icon name="AlertCircle" size={48} className="text-error" />
			</div>
			<h3 className="text-2xl font-semibold text-foreground mb-3">
				Oops! Something went wrong
			</h3>
			<p className="text-muted-foreground mb-6 max-w-md mx-auto">
				{error && "data" in error
					? JSON.stringify(error.data)
					: "We encountered an error while loading properties. Please try again."}
			</p>
			<Button onClick={() => refetch()}>
				<Icon name="RefreshCw" size={16} className="mr-2" />
				Try Again
			</Button>
		</div>
	);

	// Render loading state
	if (isLoading) {
		return (
			<section id="properties" className="py-16 bg-background">
				<div className="container mx-auto px-4 sm:px-6 lg:px-8">
					<div className="text-center mb-12">
						<h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
							Available Properties
						</h2>
						<Loader size="lg" showText text="Loading properties..." />
					</div>
					<LoadingSkeleton />
				</div>
			</section>
		);
	}

	// Render error state
	if (isError) {
		return (
			<section id="properties" className="py-16 bg-background">
				<div className="container mx-auto px-4 sm:px-6 lg:px-8">
					<ErrorState />
				</div>
			</section>
		);
	}

	// Render empty state
	if (!data || properties.length === 0) {
		return (
			<section id="properties" className="py-16 bg-background">
				<div className="container mx-auto px-4 sm:px-6 lg:px-8">
					<div className="text-center mb-12">
						<h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
							Available Properties
						</h2>
						<p className="text-lg text-muted-foreground">
							Browse properties you can rent or buy right now. No account needed
							to start exploring.
						</p>
					</div>
					<EmptyState />
				</div>
			</section>
		);
	}

	// Render property grid
	return (
		<section id="properties" className="py-16 bg-background">
			<div className="container mx-auto px-4 sm:px-6 lg:px-8">
				<div className="text-center mb-12">
					<h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
						Available Properties
					</h2>
					<p className="text-lg text-muted-foreground mb-4">
						Browse properties you can rent or buy right now. No account needed
						to start exploring.
					</p>
					<p className="text-sm text-muted-foreground">
						Showing {properties.length} of {data?.count ?? properties.length}{" "}
						properties
						{isFetching && (
							<span className="inline-flex items-center ml-2">
								<Icon name="Loader" size={14} className="animate-spin mr-1" />
								Updating...
							</span>
						)}
					</p>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
					{properties.map((property) => (
						<PropertyCard key={property.id} property={property} />
					))}
				</div>

				{/* Infinite scroll sentinel / manual load more fallback */}
				{hasMore && (
					<div ref={loadMoreRef} className="mt-12 text-center">
						<p className="text-sm text-muted-foreground mb-4">
							{isFetching
								? "Loading more properties..."
								: "Scroll to load more properties"}
						</p>
						{!("IntersectionObserver" in window) && (
							<Button
								variant="outline"
								onClick={() => setPage((prev) => prev + 1)}
								disabled={isFetching}
							>
								{isFetching ? "Loading..." : "Load More"}
							</Button>
						)}
					</div>
				)}
			</div>
		</section>
	);
};

export default PropertyGrid;
